import React from 'react';

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'left',
  fullWidth = false,
  className = '',
  onClick,
  type = 'button',
  ...props
}) => {
  // Variant styles — token-based so they follow the app's rust/graphite theme
  // and adapt to dark mode automatically via the CSS variables.
  const variantStyles = {
    primary: `
      bg-primary hover:bg-primary/90 active:bg-primary/80
      text-primary-foreground border-transparent
      focus:ring-ring focus:border-ring
      disabled:bg-muted disabled:text-muted-foreground
      shadow-sm hover:shadow-md
    `,
    secondary: `
      bg-card hover:bg-accent active:bg-accent
      text-foreground border-border
      focus:ring-ring focus:border-ring
      disabled:bg-muted disabled:text-muted-foreground disabled:border-border
      shadow-sm hover:shadow-md
    `,
    ghost: `
      bg-transparent hover:bg-accent active:bg-accent
      text-foreground border-transparent
      focus:ring-ring focus:bg-accent
      disabled:text-muted-foreground disabled:hover:bg-transparent
    `,
    danger: `
      bg-destructive hover:bg-destructive/90 active:bg-destructive/80
      text-destructive-foreground border-transparent
      focus:ring-destructive focus:border-destructive
      disabled:bg-muted disabled:text-muted-foreground
      shadow-sm hover:shadow-md
    `,
    success: `
      bg-success hover:bg-success/90 active:bg-success/80
      text-white border-transparent
      focus:ring-success focus:border-success
      disabled:bg-muted disabled:text-muted-foreground
      shadow-sm hover:shadow-md
    `
  };

  // Size styles
  const sizeStyles = {
    xs: 'px-2 py-1 text-[11px] font-medium rounded-md',
    sm: 'px-3 py-1.5 text-xs font-medium rounded-md',
    md: 'px-4 py-2 text-sm font-medium rounded-lg',
    lg: 'px-5 py-2.5 text-sm font-medium rounded-lg',
    xl: 'px-6 py-3 text-base font-medium rounded-xl'
  };

  // Icon size based on button size
  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-4 h-4',
    xl: 'w-5 h-5'
  };

  const baseStyles = `
    inline-flex items-center justify-center
    border font-medium
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:cursor-not-allowed disabled:opacity-50
    ${fullWidth ? 'w-full' : ''}
  `;

  const buttonClasses = `
    ${baseStyles}
    ${sizeStyles[size]}
    ${variantStyles[variant]}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const iconElement = icon && React.cloneElement(icon, {
    className: `${iconSizes[size]} ${loading ? 'animate-spin' : ''}`
  });

  const content = (
    <>
      {iconPosition === 'left' && iconElement && (
        <span className={children ? 'mr-2' : ''}>{iconElement}</span>
      )}
      {children && <span>{children}</span>}
      {iconPosition === 'right' && iconElement && (
        <span className={children ? 'ml-2' : ''}>{iconElement}</span>
      )}
    </>
  );

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading ? (
        <>
          <svg className={`${iconSizes[size]} animate-spin ${children ? 'mr-2' : ''}`} fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {children && <span>Loading...</span>}
        </>
      ) : (
        content
      )}
    </button>
  );
};

// Button Group Component for related actions
export const ButtonGroup = ({ children, className = '', ...props }) => {
  return (
    <div className={`inline-flex rounded-lg shadow-sm ${className}`} {...props}>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;
        
        const isFirst = index === 0;
        const isLast = index === React.Children.count(children) - 1;
        
        return React.cloneElement(child, {
          className: `
            ${child.props.className || ''}
            ${!isFirst ? '-ml-px' : ''}
            ${isFirst ? 'rounded-r-none' : ''}
            ${isLast ? 'rounded-l-none' : ''}
            ${!isFirst && !isLast ? 'rounded-none' : ''}
          `.trim().replace(/\s+/g, ' ')
        });
      })}
    </div>
  );
};

export default Button;